import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Link2,
  Link2Off,
  Plus,
  RefreshCw,
  Users,
  Search,
  Check,
  ChevronsRight,
  Sparkles,
  Zap,
} from "lucide-react";
import { toast } from "sonner";

function RepRow({ rep, onLink, onOpen, side, isSource, isSelected }) {
  const canonicalMatch = rep.canonical && rep.canonical !== rep.name;
  return (
    <div
      className={`group flex items-center justify-between gap-2 px-3 py-2 rounded-md border transition cursor-pointer ${
        isSelected
          ? "border-[hsl(var(--focus))] bg-[hsl(var(--focus))]/10"
          : "border-border hover:border-[hsl(var(--focus))]/50 bg-[hsl(var(--surface-2))]/40"
      }`}
      onClick={() => onOpen(rep, side)}
      data-testid={`rep-row-${side}-${rep.name.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{rep.name}</span>
          {rep.canonical && (
            <Badge
              variant="outline"
              className="text-[10px] border-[hsl(var(--positive))]/40 text-[hsl(var(--positive))] bg-[hsl(var(--positive))]/10"
              title={`Linked to ${rep.canonical}`}
            >
              <Link2 size={10} className="mr-1" />
              {canonicalMatch ? rep.canonical : "Canonical"}
            </Badge>
          )}
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {rep.count.toLocaleString()} {side === "ghl" ? "opps" : "subscriptions"}
        </div>
      </div>
      <Button
        size="sm"
        variant={isSource ? "default" : "ghost"}
        className="opacity-70 group-hover:opacity-100"
        onClick={(e) => {
          e.stopPropagation();
          onLink(rep, side);
        }}
        data-testid={`rep-link-${side}-${rep.name.replace(/\s+/g, "-").toLowerCase()}`}
        title={isSource ? "Cancel link" : "Link this rep"}
      >
        {isSource ? <Check size={14} /> : <ChevronsRight size={14} />}
      </Button>
    </div>
  );
}

export default function RepMapping() {
  const [data, setData] = useState({ ghl_reps: [], fr_reps: [], aliases: [] });
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [acceptingSuggestion, setAcceptingSuggestion] = useState(null);
  const [dismissed, setDismissed] = useState(new Set());

  // Linking state: you pick a "source" on one side, then click a target on the other
  const [source, setSource] = useState(null); // {rep, side}
  const [linkDialog, setLinkDialog] = useState(null); // {source, target}
  const [canonicalInput, setCanonicalInput] = useState("");

  // Canonical drawer
  const [openCanonical, setOpenCanonical] = useState(null); // alias doc

  const load = async () => {
    setLoading(true);
    try {
      const [m, s] = await Promise.all([
        api.get("/reps/mapping"),
        api.get("/reps/mapping/suggestions"),
      ]);
      setData(m.data);
      setSuggestions(s.data.items || []);
    } catch (e) {
      toast.error(`Failed to load reps: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const match = (r) => !q || r.name.toLowerCase().includes(q) || (r.canonical || "").toLowerCase().includes(q);
    return {
      ghl: (data.ghl_reps || []).filter(match),
      fr: (data.fr_reps || []).filter(match),
    };
  }, [data, query]);

  const handleRowClick = (rep, side) => {
    // If user clicks an already-linked rep → open its canonical drawer
    if (rep.canonical) {
      const canon = (data.aliases || []).find((a) => a.canonical === rep.canonical);
      if (canon) setOpenCanonical(canon);
    }
  };

  const handleLinkClick = (rep, side) => {
    if (source && source.rep.name === rep.name && source.side === side) {
      setSource(null);
      return;
    }
    if (!source) {
      setSource({ rep, side });
      toast.message(`Pick the matching rep on the ${side === "ghl" ? "FieldRoutes" : "GoHighLevel"} side`);
      return;
    }
    if (source.side === side) {
      // same side, swap source
      setSource({ rep, side });
      return;
    }
    // Two different sides — open dialog to confirm canonical name
    const target = { rep, side };
    const defaultCanonical = source.rep.canonical || target.rep.canonical || target.rep.name;
    setCanonicalInput(defaultCanonical);
    setLinkDialog({ source, target });
  };

  const confirmLink = async () => {
    if (!linkDialog) return;
    const canonical = canonicalInput.trim();
    if (!canonical) {
      toast.error("Canonical name required");
      return;
    }
    const { source, target } = linkDialog;
    try {
      // upsert both sides as aliases under the same canonical
      await api.post("/reps/alias", { canonical, alias: source.rep.name, system: source.side });
      if (target.rep.name.toLowerCase() !== canonical.toLowerCase()) {
        await api.post("/reps/alias", { canonical, alias: target.rep.name, system: target.side });
      }
      toast.success(`Linked ${source.rep.name} ↔ ${target.rep.name} as "${canonical}"`);
      setSource(null);
      setLinkDialog(null);
      setCanonicalInput("");
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleUnlink = async (canonical, alias) => {
    try {
      await api.post("/reps/unlink", { canonical, alias });
      toast.success(`Unlinked ${alias}`);
      await load();
      setOpenCanonical(null);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDeleteCanonical = async (canonical) => {
    if (!confirm(`Remove canonical rep "${canonical}" and all its aliases?`)) return;
    try {
      await api.delete(`/reps/canonical/${encodeURIComponent(canonical)}`);
      toast.success("Canonical rep removed");
      await load();
      setOpenCanonical(null);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const acceptSuggestion = async (s) => {
    const canonical = s.ghl.name; // default to GHL spelling; user can rename later via edit
    const key = `${s.ghl.name}::${s.fr.name}`;
    setAcceptingSuggestion(key);
    try {
      await api.post("/reps/alias", { canonical, alias: s.ghl.name, system: "ghl" });
      if (s.fr.name.toLowerCase() !== canonical.toLowerCase()) {
        await api.post("/reps/alias", { canonical, alias: s.fr.name, system: "fr" });
      }
      toast.success(`Linked ${s.ghl.name} ↔ ${s.fr.name}`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAcceptingSuggestion(null);
    }
  };

  const acceptAllHighConfidence = async () => {
    const high = suggestions.filter(
      (s) => s.confidence === "high" && !dismissed.has(`${s.ghl.name}::${s.fr.name}`)
    );
    if (!high.length) {
      toast.message("No high-confidence suggestions to apply");
      return;
    }
    setAcceptingSuggestion("__all__");
    try {
      for (const s of high) {
        const canonical = s.ghl.name;
        await api.post("/reps/alias", { canonical, alias: s.ghl.name, system: "ghl" });
        if (s.fr.name.toLowerCase() !== canonical.toLowerCase()) {
          await api.post("/reps/alias", { canonical, alias: s.fr.name, system: "fr" });
        }
      }
      toast.success(`Linked ${high.length} high-confidence match${high.length > 1 ? "es" : ""}`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setAcceptingSuggestion(null);
    }
  };

  const dismissSuggestion = (s) => {
    const key = `${s.ghl.name}::${s.fr.name}`;
    setDismissed(new Set([...dismissed, key]));
  };

  const resync = async () => {
    setSyncing(true);
    try {
      await api.post("/reps/recanonicalize");
      toast.success("Re-running reconciliation so KPIs pick up your mapping");
      setTimeout(async () => {
        await load();
        setSyncing(false);
      }, 12000);
    } catch (e) {
      toast.error(e.message);
      setSyncing(false);
    }
  };

  const totals = {
    ghl: (data.ghl_reps || []).length,
    fr: (data.fr_reps || []).length,
    mapped: (data.aliases || []).length,
  };

  return (
    <div className="space-y-6" data-testid="page-reps">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Rep Mapping</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Link GoHighLevel reps with FieldRoutes reps so the same person doesn't show up twice on the leaderboard.
            Click a rep on the left, then click the matching rep on the right — we'll merge them under a canonical name.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search reps…"
              className="pl-8 h-9 w-[220px]"
              data-testid="rep-search"
            />
          </div>
          <Button variant="outline" size="sm" onClick={resync} disabled={syncing} data-testid="rep-resync">
            <RefreshCw size={14} className={`mr-2 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Applying…" : "Apply & re-reconcile"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "GHL reps", value: totals.ghl, icon: Users },
          { label: "FR reps", value: totals.fr, icon: Users },
          { label: "Canonical mappings", value: totals.mapped, icon: Link2 },
        ].map((c) => (
          <Card key={c.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="size-10 rounded-md bg-[hsl(var(--surface-3))] grid place-items-center">
                <c.icon size={18} className="text-muted-foreground" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{c.label}</div>
                <div className="kpi-card-number text-2xl tabular-nums">{c.value}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Smart suggestions */}
      {suggestions.filter((s) => !dismissed.has(`${s.ghl.name}::${s.fr.name}`)).length > 0 && (
        <Card className="border-[hsl(var(--focus))]/40 bg-gradient-to-br from-[hsl(var(--focus))]/5 to-transparent">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Sparkles size={16} className="text-[hsl(var(--focus))]" />
              Smart suggestions
              <Badge variant="secondary" className="tabular-nums" data-testid="rep-suggestions-count">
                {suggestions.filter((s) => !dismissed.has(`${s.ghl.name}::${s.fr.name}`)).length}
              </Badge>
              <span className="text-[11px] font-normal text-muted-foreground ml-1">
                Fuzzy name matches across systems
              </span>
            </CardTitle>
            <Button
              size="sm"
              variant="default"
              onClick={acceptAllHighConfidence}
              disabled={acceptingSuggestion === "__all__"}
              data-testid="rep-accept-all"
            >
              <Zap size={14} className="mr-2" />
              {acceptingSuggestion === "__all__"
                ? "Applying…"
                : `Link all ${suggestions.filter((s) => s.confidence === "high" && !dismissed.has(`${s.ghl.name}::${s.fr.name}`)).length} high-confidence`}
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto pr-1">
              {suggestions
                .filter((s) => !dismissed.has(`${s.ghl.name}::${s.fr.name}`))
                .map((s) => {
                  const key = `${s.ghl.name}::${s.fr.name}`;
                  const cfgByConfidence = {
                    high: "border-[hsl(var(--positive))]/40 text-[hsl(var(--positive))] bg-[hsl(var(--positive))]/10",
                    medium: "border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] bg-[hsl(var(--warning))]/10",
                    low: "border-border text-muted-foreground bg-[hsl(var(--surface-3))]",
                  };
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-3 border border-border rounded-md p-2.5 bg-[hsl(var(--surface-2))]/60"
                      data-testid={`rep-suggestion-${key.replace(/\s+|::/g, "-").toLowerCase()}`}
                    >
                      <Badge
                        variant="outline"
                        className={`text-[10px] uppercase ${cfgByConfidence[s.confidence] || cfgByConfidence.low}`}
                      >
                        {s.score}%
                      </Badge>
                      <div className="flex-1 min-w-0 text-xs">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] uppercase text-muted-foreground w-7">GHL</span>
                          <span className="font-medium truncate">{s.ghl.name}</span>
                          <span className="text-[10px] text-muted-foreground">· {s.ghl.count} opps</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-0.5">
                          <span className="text-[10px] uppercase text-muted-foreground w-7">FR</span>
                          <span className="font-medium truncate">{s.fr.name}</span>
                          <span className="text-[10px] text-muted-foreground">· {s.fr.count} subs</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          onClick={() => acceptSuggestion(s)}
                          disabled={acceptingSuggestion === key || acceptingSuggestion === "__all__"}
                          data-testid={`rep-suggestion-accept-${key.replace(/\s+|::/g, "-").toLowerCase()}`}
                        >
                          {acceptingSuggestion === key ? (
                            <RefreshCw size={14} className="animate-spin" />
                          ) : (
                            <>
                              <Link2 size={14} className="mr-1" />
                              Link
                            </>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => dismissSuggestion(s)}
                          title="Dismiss this suggestion"
                          data-testid={`rep-suggestion-dismiss-${key.replace(/\s+|::/g, "-").toLowerCase()}`}
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 items-stretch">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Badge className="bg-[hsl(var(--info))]/20 text-[hsl(var(--info))] border border-[hsl(var(--info))]/30">GHL</Badge>
              GoHighLevel Reps
              <span className="text-muted-foreground text-xs">({filtered.ghl.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 max-h-[620px] overflow-y-auto pr-1">
            {loading ? (
              <>{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</>
            ) : filtered.ghl.length ? (
              filtered.ghl.map((r) => (
                <RepRow
                  key={`ghl-${r.name}`}
                  rep={r}
                  side="ghl"
                  onLink={handleLinkClick}
                  onOpen={handleRowClick}
                  isSource={source?.side === "ghl" && source.rep.name === r.name}
                  isSelected={source?.side === "ghl" && source.rep.name === r.name}
                />
              ))
            ) : (
              <div className="text-sm text-muted-foreground py-6 text-center">No GHL reps yet</div>
            )}
          </CardContent>
        </Card>

        <div className="hidden md:flex items-center justify-center px-2">
          <div className="rounded-full border border-border bg-[hsl(var(--surface-2))] p-3">
            <ChevronsRight size={20} className="text-muted-foreground" />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Badge className="bg-[hsl(var(--positive))]/20 text-[hsl(var(--positive))] border border-[hsl(var(--positive))]/30">FR</Badge>
              FieldRoutes Reps
              <span className="text-muted-foreground text-xs">({filtered.fr.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1.5 max-h-[620px] overflow-y-auto pr-1">
            {loading ? (
              <>{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</>
            ) : filtered.fr.length ? (
              filtered.fr.map((r) => (
                <RepRow
                  key={`fr-${r.name}`}
                  rep={r}
                  side="fr"
                  onLink={handleLinkClick}
                  onOpen={handleRowClick}
                  isSource={source?.side === "fr" && source.rep.name === r.name}
                  isSelected={source?.side === "fr" && source.rep.name === r.name}
                />
              ))
            ) : (
              <div className="text-sm text-muted-foreground py-6 text-center">No FR reps yet</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Canonical aliases list */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Link2 size={14} /> Current Mappings
            <span className="text-muted-foreground text-xs">({(data.aliases || []).length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(data.aliases || []).length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center">
              No mappings yet. Click a rep on one side then a rep on the other to link them.
            </div>
          ) : (
            <div className="space-y-2">
              {(data.aliases || []).map((a) => (
                <div
                  key={a.canonical}
                  className="flex items-center justify-between border border-border rounded-md p-3 bg-[hsl(var(--surface-2))]/40"
                  data-testid={`canonical-${a.canonical.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="size-8 rounded-md bg-[hsl(var(--positive))]/15 border border-[hsl(var(--positive))]/30 grid place-items-center">
                      <Users size={14} className="text-[hsl(var(--positive))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{a.canonical}</div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {(a.aliases || []).length
                          ? (a.aliases || []).join(" · ")
                          : "no aliases yet"}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setOpenCanonical(a)} data-testid={`canonical-edit-${a.canonical.replace(/\s+/g, "-").toLowerCase()}`}>
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Link confirm dialog */}
      <Dialog open={!!linkDialog} onOpenChange={(o) => !o && setLinkDialog(null)}>
        <DialogContent data-testid="rep-link-dialog">
          <DialogHeader>
            <DialogTitle>Link reps</DialogTitle>
          </DialogHeader>
          {linkDialog && (
            <div className="space-y-4 text-sm">
              <div className="border border-border rounded-md p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">{linkDialog.source.side === "ghl" ? "GHL" : "FR"}</span>
                  <span className="font-medium">{linkDialog.source.rep.name}</span>
                </div>
                <div className="border-t border-border my-2" />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">{linkDialog.target.side === "ghl" ? "GHL" : "FR"}</span>
                  <span className="font-medium">{linkDialog.target.rep.name}</span>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground uppercase tracking-wider">Canonical display name</label>
                <Input
                  value={canonicalInput}
                  onChange={(e) => setCanonicalInput(e.target.value)}
                  placeholder="e.g. Shawn Mason"
                  data-testid="rep-link-canonical-input"
                  autoFocus
                />
                <div className="text-[11px] text-muted-foreground">
                  This is the name that will show up on leaderboards and dashboards.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setLinkDialog(null)}>
              Cancel
            </Button>
            <Button onClick={confirmLink} data-testid="rep-link-confirm">
              <Link2 size={14} className="mr-2" /> Link them
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Canonical drawer */}
      <Dialog open={!!openCanonical} onOpenChange={(o) => !o && setOpenCanonical(null)}>
        <DialogContent data-testid="rep-canonical-dialog">
          {openCanonical && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Users size={16} /> {openCanonical.canonical}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Aliases</div>
                  {(openCanonical.aliases || []).length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No aliases linked yet.</div>
                  ) : (
                    <div className="space-y-1">
                      {openCanonical.aliases.map((a) => (
                        <div key={a} className="flex items-center justify-between border border-border rounded-md px-3 py-2">
                          <span className="text-sm">{a}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleUnlink(openCanonical.canonical, a)}
                            data-testid={`rep-unlink-${a.replace(/\s+/g, "-").toLowerCase()}`}
                          >
                            <Link2Off size={14} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {openCanonical.systems && (
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">GHL sources</div>
                      <div className="text-muted-foreground">{(openCanonical.systems.ghl || []).join(", ") || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">FR sources</div>
                      <div className="text-muted-foreground">{(openCanonical.systems.fr || []).join(", ") || "—"}</div>
                    </div>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => handleDeleteCanonical(openCanonical.canonical)}
                  className="text-[hsl(var(--negative))] hover:text-[hsl(var(--negative))]"
                  data-testid="rep-canonical-delete"
                >
                  Remove canonical rep
                </Button>
                <Button onClick={() => setOpenCanonical(null)}>Done</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
